/**
 * MOVRA product page behaviour: variant selection, add to cart (with add-ons), gallery,
 * fullscreen viewer, feature modal, sticky section nav and sticky quick-add bar.
 * All product data is read from Liquid-rendered markup (Shopify variants, media, prices).
 */

const Movra = () => window.Movra || {};
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const str = (key, fallback) => window.MovraConfig?.strings?.[key] ?? fallback;

/* ------------------------------------------------------------------ */
/* <m-product-form>                                                    */
/* ------------------------------------------------------------------ */

class MProductForm extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.querySelector('[data-m-variants]')?.textContent || '[]');
    } catch (error) {
      this.variants = [];
    }
    this.form = this.querySelector('form');
    this.idInput = this.querySelector('[data-m-variant-input]');
    this.qtyInput = this.querySelector('[data-m-qty-input]');
    this.atc = this.querySelector('[data-m-atc]');
    this.atcLabel = this.querySelector('[data-m-atc-label]');
    this.error = this.querySelector('[data-m-error]');
    this.stock = this.querySelector('[data-m-stock]');
    this.dynamic = this.querySelector('[data-m-dynamic]');
    this.lowStock = Number(this.dataset.lowStock || 0);
    this.isMain = this.dataset.context === 'main';
    this.scope = this.closest('.shopify-section') || document;

    const currentId = Number(this.idInput?.value);
    this.variant = this.variants.find((v) => v.id === currentId) || this.variants[0] || null;

    this.addEventListener('change', this.onChange);
    this.querySelectorAll('[data-m-qty]').forEach((button) =>
      button.addEventListener('click', () => this.stepQty(Number(button.dataset.mQty)))
    );
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit(this.atc);
    });
    this.updateOptionAvailability();
    if (this.isMain) this.emit();
  }

  get quantity() {
    return Math.max(1, parseInt(this.qtyInput?.value, 10) || 1);
  }

  stepQty(delta) {
    if (!this.qtyInput) return;
    const step = Number(this.qtyInput.step) || 1;
    const min = Number(this.qtyInput.min) || 1;
    const max = this.qtyInput.max ? Number(this.qtyInput.max) : Infinity;
    const next = Math.min(max, Math.max(min, this.quantity + delta * step));
    this.qtyInput.value = String(next);
    this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onChange = (event) => {
    const target = event.target;
    if (target.matches('.m-buy__radio')) {
      this.selectFromOptions();
    } else if (target.matches('[data-m-qty-input]')) {
      if (this.quantity !== Number(target.value)) target.value = String(this.quantity);
      if (this.isMain) this.emit();
    } else if (target.matches('[data-m-addon-variant]')) {
      const addon = target.closest('.m-addon');
      const check = addon?.querySelector('[data-m-addon]');
      const option = target.selectedOptions[0];
      if (check) check.value = target.value;
      const price = addon?.querySelector('[data-m-addon-price]');
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
    }
  };

  selectedOptions() {
    return [...this.querySelectorAll('[data-m-option]')].map((fieldset) => {
      const checked = fieldset.querySelector('.m-buy__radio:checked');
      const label = fieldset.querySelector('[data-m-option-selected]');
      if (label && checked) label.textContent = checked.value;
      return checked ? checked.value : null;
    });
  }

  selectFromOptions() {
    const options = this.selectedOptions();
    const match = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
    this.variant = match || null;
    this.render();
    this.updateOptionAvailability();
  }

  /** Marks option values that would lead to a sold-out or non-existent variant. */
  updateOptionAvailability() {
    const selected = this.selectedOptions();
    this.querySelectorAll('[data-m-option]').forEach((fieldset, index) => {
      fieldset.querySelectorAll('.m-buy__radio').forEach((radio) => {
        const candidate = [...selected];
        candidate[index] = radio.value;
        const variant = this.variants.find((v) => v.options.every((value, i) => value === candidate[i]));
        const available = Boolean(variant && variant.available);
        const label = this.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
        label?.classList.toggle('is-unavailable', !available);
        label?.querySelector('[data-m-unavailable-label]')?.toggleAttribute('hidden', available);
      });
    });
  }

  stockHtml(variant) {
    let state = 'in_stock';
    let text = str('in_stock', 'In stock');
    if (!variant) {
      state = 'unavailable';
      text = str('unavailable', 'Unavailable');
    } else if (!variant.available) {
      state = 'sold_out';
      text = str('sold_out', 'Sold out');
    } else if (variant.tracked && variant.qty !== null && variant.qty <= 0) {
      state = 'backorder';
      text = str('backorder', 'Available on backorder');
    } else if (variant.tracked && this.lowStock > 0 && variant.qty !== null && variant.qty <= this.lowStock) {
      state = 'low';
      text = str('low_stock', 'Only [count] left').replace('[count]', variant.qty);
    }
    const span = document.createElement('span');
    span.className = `m-stock m-stock--${state}`;
    span.innerHTML = '<span class="m-stock__dot" aria-hidden="true"></span><span></span>';
    span.lastElementChild.textContent = text;
    return span;
  }

  render() {
    const v = this.variant;
    const available = Boolean(v && v.available);
    if (this.idInput) this.idInput.value = v ? v.id : '';
    if (this.atc) this.atc.disabled = !available;
    if (this.atcLabel) {
      this.atcLabel.textContent = !v
        ? str('unavailable', 'Unavailable')
        : available
        ? `${str('add_to_cart', 'Add to cart')} · ${v.price}`
        : str('sold_out', 'Sold out');
    }
    this.dynamic?.classList.toggle('is-hidden', !available);
    if (this.stock) this.stock.replaceChildren(this.stockHtml(v));
    this.hideError();

    // Price block lives in the same section for the main product, inside this element for quick add.
    const root = this.isMain ? this.scope : this.closest('[data-m-product-scope]') || this;
    root.querySelectorAll('[data-m-price]').forEach((el) => (el.textContent = v ? v.price : ''));
    root.querySelectorAll('[data-m-compare]').forEach((el) => (el.textContent = v?.compare || ''));
    root.querySelectorAll('[data-m-compare-wrap]').forEach((el) => (el.hidden = !v?.compare));
    root.querySelectorAll('[data-m-sale-label]').forEach((el) => (el.hidden = !v?.compare));
    root.querySelectorAll('[data-m-saving]').forEach((el) => (el.textContent = v?.saving || ''));
    root.querySelectorAll('[data-m-saving-wrap]').forEach((el) => (el.hidden = !v?.saving));

    if (this.isMain && v) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }
    if (this.isMain) this.emit();
    if (v?.media) {
      const gallery = (this.isMain ? this.scope : this).querySelector('m-gallery');
      gallery?.goToMedia(v.media);
    }
  }

  emit() {
    document.dispatchEvent(
      new CustomEvent('movra:variant-change', {
        detail: { variant: this.variant, quantity: this.quantity, form: this },
      })
    );
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }

  hideError() {
    if (this.error) this.error.hidden = true;
  }

  /**
   * Adds the selected variant (and any checked add-ons) to the Shopify cart.
   * @param {HTMLElement} [trigger] - The button that started the request (for busy state)
   */
  async submit(trigger) {
    const v = this.variant;
    if (!v || !v.available || this.busy) return;
    const planInput = this.form?.querySelector('input[name="selling_plan"]:checked');
    const plan = planInput?.value || '';
    if (v.sellingPlanRequired && !plan) {
      this.showError(str('choose_options', 'Choose options'));
      return;
    }

    const lines = [{ id: v.id, quantity: this.quantity, selling_plan: plan || undefined }];
    this.querySelectorAll('[data-m-addon]:checked:not(:disabled)').forEach((checkbox) => {
      lines.push({ id: checkbox.value, quantity: 1 });
    });

    this.busy = true;
    const buttons = new Set([this.atc, trigger].filter(Boolean));
    document.querySelectorAll('[data-m-sticky-atc]').forEach((b) => this.isMain && buttons.add(b));
    buttons.forEach((b) => {
      b.setAttribute('aria-busy', 'true');
      b.disabled = true;
    });
    this.hideError();

    try {
      await Movra().addToCart(lines, { source: this.form || this });
      this.querySelectorAll('[data-m-addon]:checked').forEach((c) => (c.checked = false));
      document.dispatchEvent(new CustomEvent('movra:added', { detail: { lines } }));
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.busy = false;
      buttons.forEach((b) => {
        b.removeAttribute('aria-busy');
        b.disabled = !(this.variant && this.variant.available);
      });
    }
  }
}

/* ------------------------------------------------------------------ */
/* <m-gallery>                                                         */
/* ------------------------------------------------------------------ */

class MGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-m-track]');
    this.slides = [...(this.track?.children || [])];
    this.thumbs = [...this.querySelectorAll('[data-m-thumb]')];
    this.dots = [...this.querySelectorAll('.m-gal__dot')];
    this.current = this.querySelector('[data-m-gal-current]');
    this.prev = this.querySelector('[data-m-gal-prev]');
    this.next = this.querySelector('[data-m-gal-next]');
    this.viewer = this.querySelector('[data-m-viewer]');
    this.index = 0;
    if (!this.track) return;

    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.go(Number(thumb.dataset.mThumb)))
    );
    this.prev?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(this.index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(this.index - 1);
      }
    });
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.querySelectorAll('[data-m-open-viewer]').forEach((button) =>
      button.addEventListener('click', () => {
        const idx = button.dataset.mOpenViewer;
        this.openViewer(idx === '' || idx === undefined ? this.index : Number(idx));
      })
    );
    this.setupViewer();
    if (this.querySelector('model-viewer') && window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0' }]);
    }
    const initial = Math.min(Number(this.dataset.initial || 0), this.slides.length - 1);
    if (initial > 0) {
      requestAnimationFrame(() => {
        this.track.scrollLeft = initial * this.track.clientWidth;
        this.update(initial);
      });
    } else {
      this.update(0);
    }
  }

  onScroll = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      const width = this.track.clientWidth || 1;
      const idx = Math.round(this.track.scrollLeft / width);
      if (idx !== this.index) this.update(idx);
    });
  };

  go(index) {
    const total = this.slides.length;
    if (!total) return;
    const idx = Math.max(0, Math.min(total - 1, index));
    this.track.scrollTo({ left: idx * this.track.clientWidth, behavior: reduced.matches ? 'auto' : 'smooth' });
    this.update(idx);
  }

  goToMedia(mediaId) {
    const idx = this.slides.findIndex((slide) => Number(slide.dataset.mediaId) === Number(mediaId));
    if (idx >= 0) this.go(idx);
  }

  update(idx) {
    const previous = this.slides[this.index];
    previous?.querySelectorAll('video').forEach((video) => video.pause());
    this.index = idx;
    this.thumbs.forEach((thumb, i) => {
      if (i === idx) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    this.thumbs[idx]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === idx));
    if (this.current) this.current.textContent = String(idx + 1);
    if (this.prev) this.prev.disabled = idx === 0;
    if (this.next) this.next.disabled = idx === this.slides.length - 1;
  }

  setupViewer() {
    if (!this.viewer) return;
    this.vTrack = this.viewer.querySelector('[data-m-viewer-track]');
    this.vSlides = [...(this.vTrack?.children || [])];
    this.vCurrent = this.viewer.querySelector('[data-m-viewer-current]');
    this.zoomBtn = this.viewer.querySelector('[data-m-viewer-zoom]');
    this.viewer.querySelector('[data-m-viewer-close]')?.addEventListener('click', () => this.viewer.close());
    this.viewer.querySelector('[data-m-viewer-prev]')?.addEventListener('click', () => this.viewerGo(this.vIndex - 1));
    this.viewer.querySelector('[data-m-viewer-next]')?.addEventListener('click', () => this.viewerGo(this.vIndex + 1));
    this.zoomBtn?.addEventListener('click', () => this.toggleZoom());
    this.vTrack?.addEventListener('click', (event) => {
      const img = event.target.closest('.m-viewer__img:not(.m-viewer__img--preview)');
      if (img) this.toggleZoom(event, img);
    });
    this.vTrack?.addEventListener(
      'scroll',
      () => {
        if (this.vFrame) return;
        this.vFrame = requestAnimationFrame(() => {
          this.vFrame = null;
          const idx = Math.round(this.vTrack.scrollLeft / (this.vTrack.clientWidth || 1));
          if (idx !== this.vIndex) this.viewerUpdate(idx);
        });
      },
      { passive: true }
    );
    this.viewer.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.viewerGo(this.vIndex + 1);
      if (event.key === 'ArrowLeft') this.viewerGo(this.vIndex - 1);
    });
  }

  loadViewerImage(slide) {
    const img = slide?.querySelector('img[data-src]');
    if (!img) return;
    img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
  }

  openViewer(index) {
    if (!this.viewer) return;
    Movra().openDialog(this.viewer, {
      initialFocus: '[data-m-viewer-close]',
      onClose: () => {
        this.resetZoom();
        this.go(this.vIndex);
      },
    });
    this.vSlides.forEach((slide, i) => {
      if (Math.abs(i - index) <= 1) this.loadViewerImage(slide);
    });
    requestAnimationFrame(() => {
      this.vTrack.scrollTo({ left: index * this.vTrack.clientWidth, behavior: 'auto' });
      this.viewerUpdate(index);
    });
  }

  viewerGo(index) {
    const idx = Math.max(0, Math.min(this.vSlides.length - 1, index));
    this.resetZoom();
    this.vTrack.scrollTo({ left: idx * this.vTrack.clientWidth, behavior: reduced.matches ? 'auto' : 'smooth' });
    this.viewerUpdate(idx);
  }

  viewerUpdate(idx) {
    this.vIndex = idx;
    if (this.vCurrent) this.vCurrent.textContent = String(idx + 1);
    [idx - 1, idx, idx + 1].forEach((i) => this.loadViewerImage(this.vSlides[i]));
  }

  toggleZoom(event, imgEl) {
    const img = imgEl || this.vSlides[this.vIndex]?.querySelector('.m-viewer__img:not(.m-viewer__img--preview)');
    if (!img) return;
    const zoomed = !img.classList.contains('is-zoomed');
    if (zoomed && event && event.clientX) {
      const rect = img.getBoundingClientRect();
      img.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${
        ((event.clientY - rect.top) / rect.height) * 100
      }%`;
    } else if (zoomed) {
      img.style.transformOrigin = '50% 50%';
    }
    img.classList.toggle('is-zoomed', zoomed);
    this.vTrack.classList.toggle('is-zoomed', zoomed);
    if (this.zoomBtn) {
      this.zoomBtn.setAttribute('aria-pressed', String(zoomed));
      this.zoomBtn.textContent = zoomed ? str('zoom_out', 'Zoom out') : str('zoom_in', 'Zoom in');
    }
  }

  resetZoom() {
    this.vSlides?.forEach((slide) => slide.querySelector('.is-zoomed')?.classList.remove('is-zoomed'));
    this.vTrack?.classList.remove('is-zoomed');
    if (this.zoomBtn) {
      this.zoomBtn.setAttribute('aria-pressed', 'false');
      this.zoomBtn.textContent = str('zoom_in', 'Zoom in');
    }
  }
}

/* ------------------------------------------------------------------ */
/* <m-feature-cards> – modal with previous/next and swipe              */
/* ------------------------------------------------------------------ */

class MFeatureCards extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-m-feature-dialog]');
    this.templates = [...this.querySelectorAll('template[data-m-feature]')];
    this.body = this.dialog?.querySelector('[data-m-feature-body]');
    this.counter = this.dialog?.querySelector('[data-m-feature-counter]');
    if (!this.dialog) return;
    this.querySelectorAll('[data-m-feature-open]').forEach((button) =>
      button.addEventListener('click', () => this.open(Number(button.dataset.mFeatureOpen)))
    );
    this.dialog.querySelector('[data-m-feature-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.querySelector('[data-m-feature-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.dialog.querySelector('[data-m-feature-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.dialog.addEventListener('keydown', (event) => {
      if (event.target.closest('input, textarea, select')) return;
      if (event.key === 'ArrowRight') this.show(this.index + 1);
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
    });
    let startX = null;
    let startY = null;
    this.dialog.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );
    this.dialog.addEventListener(
      'touchend',
      (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
        startX = null;
      },
      { passive: true }
    );
  }

  open(index) {
    this.show(index);
    Movra().openDialog(this.dialog, { initialFocus: '[data-m-feature-close]' });
  }

  show(index) {
    const total = this.templates.length;
    if (!total) return;
    this.index = (index + total) % total;
    this.body.replaceChildren(this.templates[this.index].content.cloneNode(true));
    this.body.scrollTop = 0;
    const title = this.body.querySelector('[data-m-feature-title]');
    if (title) {
      title.id = `${this.dialog.id}-title`;
      this.dialog.setAttribute('aria-labelledby', title.id);
    }
    if (this.counter) this.counter.textContent = `${this.index + 1} / ${total}`;
  }
}

/* ------------------------------------------------------------------ */
/* <m-product-sticky> – section nav + quick-add bar                    */
/* ------------------------------------------------------------------ */

class MProductSticky extends HTMLElement {
  connectedCallback() {
    this.nav = this.querySelector('[data-m-subnav]');
    this.bar = this.querySelector('[data-m-quickbar]');
    this.links = [...this.querySelectorAll('[data-m-subnav-link]')];
    this.form = document.querySelector('m-product-form[data-context="main"]');
    this.mainAtc = this.form?.querySelector('[data-m-atc]');
    this.state = { pastProduct: false, atcHidden: false, blocked: false };

    // Show the section nav once the main product area has scrolled away.
    const productSection = document.querySelector('.m-pdp');
    if (productSection && this.nav) {
      this.navIo = new IntersectionObserver(([entry]) => {
        this.state.pastProduct = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sync();
      });
      this.navIo.observe(productSection);
    }
    if (this.mainAtc && this.bar) {
      this.atcIo = new IntersectionObserver(([entry]) => {
        this.state.atcHidden = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sync();
      });
      this.atcIo.observe(this.mainAtc);
    }

    // Active link underline via IntersectionObserver on each target section.
    this.links.forEach((link) => {
      const target = document.getElementById(link.getAttribute('href').slice(1));
      const hidden = !target || Boolean(target.closest('[hidden]'));
      link.closest('li')?.toggleAttribute('hidden', hidden);
    });
    this.links = this.links.filter((link) => !link.closest('li')?.hasAttribute('hidden'));
    this.targets = this.links
      .map((link) => document.getElementById(link.getAttribute('href').slice(1)))
      .filter(Boolean);
    this.visible = new Map();
    this.secIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => this.visible.set(entry.target.id, entry.isIntersecting));
        const active = this.targets.find((t) => this.visible.get(t.id));
        this.links.forEach((link) => {
          const on = active && link.getAttribute('href') === `#${active.id}`;
          if (on) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
          if (on) link.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      },
      { rootMargin: '-35% 0px -55% 0px' }
    );
    this.targets.forEach((t) => this.secIo.observe(t));

    this.querySelectorAll('[data-m-sticky-atc]').forEach((button) =>
      button.addEventListener('click', () => this.form?.submit(button))
    );
    this.querySelectorAll('[data-m-sticky-qty]').forEach((button) =>
      button.addEventListener('click', () => this.form?.stepQty(Number(button.dataset.mStickyQty)))
    );

    document.addEventListener('movra:variant-change', this.onVariant);
    document.addEventListener('theme-drawer:open', this.onBlock);
    document.addEventListener('theme-drawer:close', this.onBlock);
    this.mo = new MutationObserver(this.onBlock);
    this.mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    if (this.form?.variant) this.onVariant({ detail: { variant: this.form.variant, quantity: this.form.quantity } });
  }

  disconnectedCallback() {
    this.navIo?.disconnect();
    this.atcIo?.disconnect();
    this.secIo?.disconnect();
    this.mo?.disconnect();
    document.removeEventListener('movra:variant-change', this.onVariant);
    document.removeEventListener('theme-drawer:open', this.onBlock);
    document.removeEventListener('theme-drawer:close', this.onBlock);
    document.documentElement.classList.remove('m-subnav-active');
  }

  onBlock = () => {
    const drawerOpen = Boolean(document.querySelector('theme-drawer[open]'));
    const modalOpen = document.documentElement.classList.contains('m-modal-open');
    const blocked = drawerOpen || modalOpen;
    if (blocked !== this.state.blocked) {
      this.state.blocked = blocked;
      this.sync();
    }
  };

  onVariant = (event) => {
    const { variant, quantity } = event.detail || {};
    this.querySelectorAll('[data-m-sticky-variant]').forEach((el) => (el.textContent = variant ? variant.title : ''));
    this.querySelectorAll('[data-m-sticky-price]').forEach((el) => (el.textContent = variant ? variant.price : ''));
    this.querySelectorAll('[data-m-sticky-qty-value]').forEach((el) => (el.textContent = quantity || 1));
    const available = Boolean(variant && variant.available);
    this.querySelectorAll('[data-m-sticky-atc]').forEach((button) => {
      button.disabled = !available;
      const label = button.querySelector('[data-m-label]');
      if (label) label.textContent = available ? button.dataset.labelAdd : str('sold_out', 'Sold out');
    });
    const img = this.querySelector('[data-m-sticky-img]');
    if (img && variant?.image) img.src = variant.image;
  };

  sync() {
    const { pastProduct, atcHidden, blocked } = this.state;
    const showNav = pastProduct && !blocked;
    const showBar = atcHidden && !blocked;
    if (this.nav) {
      this.nav.toggleAttribute('data-visible', showNav);
      this.nav.inert = !showNav;
    }
    if (this.bar) {
      this.bar.toggleAttribute('data-visible', showBar);
      this.bar.inert = !showBar;
    }
    document.documentElement.classList.toggle('m-subnav-active', showNav);
    document.documentElement.style.setProperty('--m-subnav-h', showNav ? `${this.nav.offsetHeight}px` : '0px');
  }
}

/* ------------------------------------------------------------------ */
/* <m-recommendations> – Shopify Product Recommendations API           */
/* ------------------------------------------------------------------ */

class MRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.loaded) return;
    this.io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.io.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  async load() {
    this.loaded = true;
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(this.dataset.url);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('m-recommendations');
      if (fresh && fresh.querySelector('[data-m-rec-item]')) {
        this.innerHTML = fresh.innerHTML;
      } else {
        this.closest('.shopify-section')?.setAttribute('hidden', '');
      }
    } catch (error) {
      this.closest('.shopify-section')?.setAttribute('hidden', '');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

/* Any element with [data-m-submit-main] adds the main product form's selected variant. */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-m-submit-main]');
  if (!button) return;
  const form = document.querySelector('m-product-form[data-context="main"]');
  if (form) form.submit(button);
});

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};
define('m-product-form', MProductForm);
define('m-gallery', MGallery);
define('m-feature-cards', MFeatureCards);
define('m-product-sticky', MProductSticky);
define('m-recommendations', MRecommendations);
